/** Examination Chamber — routes, backend contract values, UX thresholds. */
import { findExamPaper } from './data/examPaperCatalog';
import { EXAM_BAC_PATH, EXAM_CATALOG_PATH } from '../../constants/examSetup';

export const EXAM_BASE_PATH = '/examens';
export const examDiplomaPath = (slug, query = '') => `${EXAM_BASE_PATH}/${slug}${query ? `?${query}` : ''}`;
export const examSessionPath = (sessionId) => `${EXAM_BASE_PATH}/epreuve/${encodeURIComponent(sessionId)}`;
export const examResultPath = (resultId) => `${EXAM_BASE_PATH}/resultat/${encodeURIComponent(resultId)}`;

export const DIPLOMAS = Object.freeze([
  Object.freeze({ slug: 'brevet', type: 'Brevet' }),
  Object.freeze({ slug: 'bac-premiere', type: 'Bac Première' }),
  Object.freeze({ slug: 'bac-terminale', type: 'Bac Terminale' }),
]);
/**
 * The diploma a pupil of this class actually sits (RVW1 V15).
 *
 * One tile in Défi express, never three: 3e sits the brevet, Première the
 * anticipated bac, Terminale the bac. Every other class sits none — a 5e offered
 * a brevet paper is a promise the corpus cannot keep — and gets no tile at all.
 */
const DIPLOMA_BY_CLASS = Object.freeze({
  '3e': 'brevet', 'Troisième': 'brevet',
  '1ère': 'bac-premiere', '1ere': 'bac-premiere', 'Première': 'bac-premiere',
  'Terminale': 'bac-terminale',
});

/** Brand key (constants/brand.js) per diploma slug; the hub and assigned exams are EXAM. */
export const DIPLOMA_BRAND = Object.freeze({ brevet: 'BREVET', 'bac-premiere': 'BAC_PREMIERE', 'bac-terminale': 'BAC' });
export const diplomaBrand = (slug) => DIPLOMA_BRAND[slug] || 'EXAM';

export const diplomaBySlug = (slug) => DIPLOMAS.find((diploma) => diploma.slug === slug) || null;
export const diplomaForClass = (classLevel) => diplomaBySlug(DIPLOMA_BY_CLASS[classLevel] || '');
export const diplomaByType = (type) => DIPLOMAS.find((diploma) => diploma.type === type) || null;
const SUBJECT_DIPLOMA_PREFIX = Object.freeze({ premiere: 'bac-premiere', terminale: 'bac-terminale' });
export const diplomaBySubjectId = (subjectId) => {
  const parts = String(subjectId || '').split(':');
  const slug = parts[0] === 'mixed' ? parts[1] : parts[0];
  return diplomaBySlug(SUBJECT_DIPLOMA_PREFIX[slug] || slug);
};

/**
 * The public chamber and the teacher workspace share the /examens prefix.
 * Only the paths below are public; every other segment (nouveau,
 * attributions, passer, …) belongs to the authenticated app or to its own
 * standalone block. An allow-list, never a prefix match: /examens/:diploma
 * used to swallow /examens/nouveau and would swallow the next route too.
 */
const PUBLIC_EXAM_SEGMENTS = Object.freeze(['epreuve', 'resultat']);

export const isPublicExamPath = (pathname) => {
  const path = String(pathname || '').replace(/\/+$/, '');
  if (path === EXAM_CATALOG_PATH || path === EXAM_BAC_PATH) return true;
  if (!/^\/examens(\/|$)/i.test(path)) return false;
  const segments = path.split('/').filter(Boolean).slice(1).map((s) => s.toLowerCase());
  if (segments.length === 0) return true;
  if (PUBLIC_EXAM_SEGMENTS.includes(segments[0])) return true;
  if (segments.length === 1) return Boolean(diplomaBySlug(segments[0]));
  return segments.length === 3 && Boolean(findExamPaper(segments[0], segments[1], segments[2]));
};

export const SESSION_MODE = Object.freeze({ FULL: 'full', SIMULATION: 'simulation_30', CHAPTER: 'chapter_review' });
export const SESSION_MODE_ORDER = Object.freeze([SESSION_MODE.FULL, SESSION_MODE.SIMULATION, SESSION_MODE.CHAPTER]);
export const SESSION_STATUS = Object.freeze({ ACTIVE: 'active', SUBMITTED: 'submitted', EXPIRED: 'expired' });
export const CONTENT_SOURCE = Object.freeze({ OFFICIAL: 'official', MIXED: 'mixed' });
export const CONTENT_SOURCE_ORDER = Object.freeze([CONTENT_SOURCE.OFFICIAL, CONTENT_SOURCE.MIXED]);
export const RUN_STATUS = Object.freeze({ LOADING: 'loading', READY: 'ready', LOCKED: 'locked', ERROR: 'error' });
export const RESPONSE_MODE = Object.freeze({
  QCM: 'qcm', MULTI: 'multi_select', NUMERIC: 'numeric', FREE: 'free_text',
});
export const VERDICT = Object.freeze({ CORRECT: 'correct', WRONG: 'wrong', BLANK: 'blank', MANUAL: 'manual' });
export const SCORING_KIND = Object.freeze({
  OFFICIAL: 'official_weighted', ESTIMATED: 'estimated_automatic', UNSCORED: 'unscored',
});
export const SAVE_STATE = Object.freeze({ IDLE: 'idle', SAVING: 'saving', SAVED: 'saved', RETRYING: 'retrying' });
export const ITEM_STATE = Object.freeze({ ANSWERED: 'answered', EMPTY: 'empty', FLAGGED: 'flagged' });

export const ATMOSPHERE = Object.freeze({ SOBRE: 'sobre', TENSION: 'tension', IMMERSIF: 'immersif' });
export const TEXT_SIZE = Object.freeze({ NORMAL: 'normal', LARGE: 'large' });
export const MOTION = Object.freeze({ AUTO: 'auto', REDUCED: 'reduced' });
export const SOUND = Object.freeze({ OFF: 'off', ON: 'on' });
export const DEFAULT_PREFS = Object.freeze({
  atmosphere: ATMOSPHERE.TENSION,
  textSize: TEXT_SIZE.NORMAL,
  motion: MOTION.AUTO,
  sound: SOUND.OFF,
  candidateName: '',
});

/** Official national subjects raise the room; a mixed draft stays a practice room. */
export const STAKES = Object.freeze({ NATIONAL: 'national', PRACTICE: 'practice' });

/** Convocation overlay: one beat per line, then the drawn session, then the paper. */
export const CONVOCATION_STEP = Object.freeze({ SUMMONS: 'summons', DURATION: 'duration', DRAWN: 'drawn' });
export const CONVOCATION_STEP_ORDER = Object.freeze([
  CONVOCATION_STEP.SUMMONS, CONVOCATION_STEP.DURATION, CONVOCATION_STEP.DRAWN,
]);
export const CONVOCATION_BEAT_MS = 900;
export const CONVOCATION_HOLD_MS = 1100;

export const CLOCK_PHASE = Object.freeze({ CALM: 'calm', TENSION: 'tension', CRITICAL: 'critical', OVER: 'over' });
export const CLOCK_TICK_MS = 1000;
export const TENSION_RATIO = 0.1;
export const CRITICAL_SECONDS = 5 * 60;
export const LIGHT_STEPS = 10;
export const CLOCK_ANNOUNCE_SECONDS = Object.freeze([30 * 60, 10 * 60, 5 * 60, 60]);

export const CANDIDATE_SAVE_MS = 400;
export const AUTOSAVE_DEBOUNCE_MS = 1200;
export const AUTOSAVE_RETRY_MS = 5000;
export const HTTP_CONFLICT = 409;

export const STORE_KEY = 'neoprof:exam-chamber';
export const STORE_VERSION = 1;
export const STORE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // server result retention

export const QUERY = Object.freeze({
  SUBJECT: 'sujet', SOURCE: 'source', MODE: 'mode', CHAPTER: 'chapitre', STEP: 'etape', REPLAY: 'rejouer',
});
export const STEP_BRIEFING = 'briefing';

export const WEAK_CHAPTER_RATIO = 0.5;
export const EXCEPTIONAL_RATIO = 0.8;
export const NOTE_SCALE = 20;
export const CELEBRATION = Object.freeze({ COMPLETE: 'comet', EXCEPTIONAL: 'supernova' });
